/**
 * Filestack CDN URL helpers.
 * Prefer resize=width transforms so Delivery bills resized bytes, not multi‑MB originals.
 */

export const FILESTACK_CDN_BASE = "https://cdn.filestackcontent.com"

export const STOCK_HERO_IMAGES_S3_BASE = "https://healme-hero-images.s3.amazonaws.com"

/** Mirrors Filestack::HandleExtractor.handle_like? in Ruby. */
export function isFilestackHandle(candidate: string | null | undefined): boolean {
  if (!candidate || typeof candidate !== "string") return false
  const trimmed = candidate.trim()
  return trimmed.length >= 5 && trimmed.length <= 128 && /^[A-Za-z0-9_-]+$/.test(trimmed)
}

/** Common display widths (CSS px × ~2 for retina). */
export const FILESTACK_CDN_WIDTHS = {
  thumb: 400,
  card: 800,
  gallery: 1200,
  lightbox: 1920
} as const

export type FilestackCdnFit = "max" | "clip" | "crop"

export type FilestackPhotoHandles = {
  original?: { any?: string; webp?: string; jpeg?: string }
  x_large?: { webp?: string; jpeg?: string }
  large?: { webp?: string; jpeg?: string }
  medium?: { webp?: string; jpeg?: string }
  square?: { webp?: string; jpeg?: string }
  small?: { webp?: string; jpeg?: string }
}

/**
 * Extract Filestack handle from a CDN URL (with or without transforms).
 * e.g. "https://cdn.filestackcontent.com/resize=width:800,fit:max/ABC123" -> "ABC123"
 */
export function getFilestackHandleFromUrl(url: string | null | undefined): string | null {
  if (!url || typeof url !== "string" || !url.includes("cdn.filestackcontent.com")) return null
  const parts = url.split("/").filter(Boolean)
  const handle = parts[parts.length - 1]
  return handle || null
}

/**
 * Build a CDN URL. When `width` is set, applies resize=width:N,fit:max (Delivery-friendly).
 * Accepts a bare handle or an existing Filestack CDN URL.
 */
export function filestackCdnUrl(
  handleOrUrl: string | null | undefined,
  options?: { width?: number; fit?: FilestackCdnFit }
): string | null {
  if (!handleOrUrl || typeof handleOrUrl !== "string") return null

  const handle = handleOrUrl.includes("cdn.filestackcontent.com")
    ? getFilestackHandleFromUrl(handleOrUrl)
    : handleOrUrl.trim()
  if (!handle) return null

  if (!isFilestackHandle(handle)) {
    return `${STOCK_HERO_IMAGES_S3_BASE}/${handle}`
  }

  const width = options?.width
  const fit = options?.fit ?? "max"
  if (width && width > 0) {
    return `${FILESTACK_CDN_BASE}/resize=width:${width},fit:${fit}/${handle}`
  }
  return `${FILESTACK_CDN_BASE}/${handle}`
}

/**
 * Prefer display-sized derivatives, then fall back to original.
 * With CDN resize, either handle is fine; large/webp first avoids pulling huge originals when possible.
 */
export function pickFilestackHandle(photoHandles: FilestackPhotoHandles | null | undefined): string | null {
  if (!photoHandles) return null
  return (
    photoHandles.large?.webp ||
    photoHandles.large?.jpeg ||
    photoHandles.x_large?.webp ||
    photoHandles.x_large?.jpeg ||
    photoHandles.original?.any ||
    photoHandles.original?.webp ||
    photoHandles.original?.jpeg ||
    photoHandles.medium?.webp ||
    photoHandles.medium?.jpeg ||
    photoHandles.square?.webp ||
    photoHandles.square?.jpeg ||
    photoHandles.small?.webp ||
    null
  )
}

/** Highest-quality source handle (lightbox / zoom). Still pair with a width when serving. */
export function pickFilestackHandleHighest(photoHandles: FilestackPhotoHandles | null | undefined): string | null {
  if (!photoHandles) return null
  return (
    photoHandles.original?.any ||
    photoHandles.original?.webp ||
    photoHandles.original?.jpeg ||
    photoHandles.x_large?.webp ||
    photoHandles.x_large?.jpeg ||
    photoHandles.large?.webp ||
    photoHandles.large?.jpeg ||
    photoHandles.medium?.webp ||
    photoHandles.medium?.jpeg ||
    photoHandles.square?.webp ||
    photoHandles.square?.jpeg ||
    null
  )
}

export function filestackUrlFromPhotoHandles(
  photoHandles: FilestackPhotoHandles | null | undefined,
  options?: { width?: number; fit?: FilestackCdnFit; preferOriginal?: boolean }
): string | null {
  const handle = options?.preferOriginal
    ? pickFilestackHandleHighest(photoHandles)
    : pickFilestackHandle(photoHandles)
  return filestackCdnUrl(handle, { width: options?.width, fit: options?.fit })
}

/**
 * If `url` is a Filestack CDN URL, rewrite with resize. Otherwise return unchanged (e.g. S3).
 */
export function ensureFilestackCdnResized(
  url: string | null | undefined,
  options?: { width?: number; fit?: FilestackCdnFit }
): string | null | undefined {
  if (!url || typeof url !== "string") return url
  if (!url.includes("cdn.filestackcontent.com")) return url
  return filestackCdnUrl(url, options) || url
}
